import type { InsertAttestationCycleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertAttestationCycleDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { InsertAttestationConfigInput } from './useInsertAttestationConfig';

/**
 * Public input for the `insertAttestationCycle` mutation. Mirrors the tRPC
 * `attestationCycle.create` input so consumers can forward the same object to
 * either path.
 */
export interface InsertAttestationCycleInput {
  DocumentId: string;
  AllowCarryForward: boolean;
  AttestationConfig?: InsertAttestationConfigInput | null;
}

/**
 * Maps the tRPC mutation response to match the GraphQL mutation response shape.
 * The legacy GraphQL mutation returned BOTH the new cycle Id and the upserted
 * config Id. The tRPC `create` returns the cycle Id; the config is upserted
 * against `AttestationConfig.ParentId`, so we surface that as the config Id.
 */
const mapTrpcResponseToGraphQL = (
  data: { Id: string },
  input: InsertAttestationCycleInput
): InsertAttestationCycleMutation => ({
  insertChildAttestationCycle: { Id: data.Id },
  insertChildAttestationConfig: input.AttestationConfig
    ? { Id: input.AttestationConfig.ParentId }
    : undefined,
});

export const useInsertAttestationCycle = createMutationHook({
  name: 'insertAttestationCycle',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.attestationCycle.create.mutationOptions({ onSuccess }),
  mapInput: (input: InsertAttestationCycleInput) => ({
    DocumentId: input.DocumentId,
    AllowCarryForward: input.AllowCarryForward,
    AttestationConfig: input.AttestationConfig,
  }),
  mapResponse: (result: { Id: string }, input: InsertAttestationCycleInput) =>
    mapTrpcResponseToGraphQL(result, input),
  // The attestations Tab re-derives its view from three read families: the
  // document read (attestation config), the attestation-cycle reads, and the
  // attestation register. Invalidate all three routers so the Tab reflects
  // the new cycle + config after save — matching the Apollo path, which
  // re-fetched via those reads with no inline cache update.
  invalidate: (trpc) => [
    trpc.frontend.document.pathKey(),
    trpc.frontend.attestationCycle.pathKey(),
    trpc.frontend.attestation.pathKey(),
  ],
  graphqlDocument: InsertAttestationCycleDocument,
  graphqlVariables: (input: InsertAttestationCycleInput) => ({
    DocumentId: input.DocumentId,
    AllowCarryForward: input.AllowCarryForward,
    attestationConfig: input.AttestationConfig
      ? {
          ParentId: input.AttestationConfig.ParentId,
          RequireGlobalAttestation:
            input.AttestationConfig.RequireGlobalAttestation,
          AttestationGroupIds: input.AttestationConfig.AttestationGroupIds,
          AttestationPromptText: input.AttestationConfig.AttestationPromptText,
          AttestationTimeLimit: input.AttestationConfig.AttestationTimeLimit,
        }
      : undefined,
  }),
});
